/**
 * Count videos from courses/chapters/lessons.
 * Matches Academy 1.0: each lesson with video_lesson (length > 0) counts as 1 video.
 * Does not use video_lesson.length to avoid over-counting when a lesson has multiple quality variants.
 */
export function getVideosCountFromCourses(courses: {
  course?: Array<{
    chapters?: Array<{ lessons?: { lessons?: Array<{ video_lesson?: unknown[] }> } }>;
  }>;
}): number {
  const courseList = courses?.course;
  if (!Array.isArray(courseList)) return 0;
  return courseList.reduce((total, course) => {
    const courseVideos =
      course.chapters?.reduce((chapterTotal, chapter) => {
        const lessonVideos = (chapter.lessons?.lessons ?? []).filter(
          (lesson) => (lesson.video_lesson?.length ?? 0) > 0
        ).length;
        return chapterTotal + lessonVideos;
      }, 0) ?? 0;
    return total + courseVideos;
  }, 0);
}

/**
 * Count of courses that have at least one quiz lesson (1 quiz per course that has a quiz).
 * A lesson is considered a quiz if is_quiz === true or it has quiz content (question_count or assessment).
 */
type LessonLike = {
  is_quiz?: boolean;
  quiz?: { question_count?: number; assessment?: unknown[] };
};
type ChapterLike = { lessons?: { lessons?: LessonLike[] } };
type CourseLike = { chapters?: ChapterLike[] };

export function getCoursesWithQuizCount(courses: {
  course?: CourseLike[] | unknown[];
}): number {
  const courseList = courses?.course;
  if (!Array.isArray(courseList)) return 0;
  return courseList.filter((course: unknown) => {
    const c = course as CourseLike;
    const hasQuizLesson = c.chapters?.some((chapter) =>
      (chapter.lessons?.lessons ?? []).some(
        (lesson) =>
          lesson?.is_quiz === true ||
          (lesson?.quiz &&
            ((lesson.quiz.question_count ?? 0) > 0 ||
              (lesson.quiz.assessment?.length ?? 0) > 0))
      )
    );
    return !!hasQuizLesson;
  }).length;
}
